import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PublicNav from '@/components/PublicNav';
import PublicFooter from '@/components/PublicFooter';
import { useSEO } from '@/hooks/useSEO';
import { usePageTracker } from '@/hooks/usePageTracker';
import {
  ArrowRight, Brain, Database, Globe, Shield, Zap,
  Building2, FlaskConical, BarChart3, Target, FileText,
  Search, Handshake, Lock, Layers, CheckCircle2,
  Plane, Radio, Activity, MapPin, Calendar
} from 'lucide-react';

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function PlatformHero() {
  return (
    <section className="relative pt-32 lg:pt-40 pb-20 lg:pb-28 bg-[#0A1128] overflow-hidden" data-testid="platform-hero">
      <div className="absolute inset-0 grid-bg" />
      <div className="absolute top-1/3 -right-40 w-[500px] h-[500px] bg-[#00709B]/12 rounded-full blur-[120px] pulse-glow" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#70C24C]/30 bg-[#70C24C]/5 text-[#70C24C] text-xs font-bold uppercase tracking-[0.2em] mb-6 reveal-up">
            <Radio className="w-3.5 h-3.5 animate-pulse" />
            Living Intelligence Engine
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tighter leading-[1.05] reveal-up stagger-1" data-testid="platform-heading">
            How a living platform
            <br />
            <span className="shimmer-text">actually works.</span>
          </h1>
          <p className="mt-8 text-lg lg:text-xl text-[#A1A8C3] font-light leading-relaxed max-w-2xl reveal-up stagger-2">
            Most platforms are databases with a search bar. ForesightRx is a continuously evolving
            intelligence engine — fed by boots-on-ground conference attendance, powered by AI matching,
            and delivered through human-facilitated deal execution.
          </p>
        </div>
      </div>
    </section>
  );
}

function TheLivingLoop() {
  const [ref, inView] = useInView();
  const loop = [
    { icon: <Plane className="w-6 h-6" />, title: 'Attend', desc: 'We travel to 12+ conferences annually — BIO International, BioChina, BIO-Europe, CPHI. We meet founders face-to-face.', color: '#70C24C' },
    { icon: <Search className="w-6 h-6" />, title: 'Qualify', desc: 'We assess the science, the team, the data. Not every biotech makes it into our system. Quality over quantity, always.', color: '#00709B' },
    { icon: <Database className="w-6 h-6" />, title: 'Index', desc: 'Qualified assets enter our living database — tagged by therapeutic area, stage, modality, geography, and deal readiness.', color: '#70C24C' },
    { icon: <Brain className="w-6 h-6" />, title: 'Match', desc: 'Our AI maps indexed assets against active pharma BD theses in real time. Months of scouting compressed to minutes. High-fit matches surface automatically.', color: '#00709B' },
    { icon: <Handshake className="w-6 h-6" />, title: 'Facilitate', desc: 'Warm introductions, CDAs, NCDs, meetings — all orchestrated. No cold outreach. No waiting. Deals move faster when both sides are pre-qualified.', color: '#70C24C' },
    { icon: <Activity className="w-6 h-6" />, title: 'Learn', desc: 'Every engagement enriches the intelligence. Better matching, faster qualification, deeper insights. The loop compounds.', color: '#00709B' },
  ];

  return (
    <section ref={ref} className="py-24 lg:py-32 bg-[#111A3A]" data-testid="living-loop-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className={`text-center mb-16 ${inView ? 'reveal-up' : 'opacity-0'}`}>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-4">The Living Loop</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-5">
            Attend. Qualify. Index. Match. Facilitate. Learn. Repeat.
          </h2>
          <p className="text-lg text-[#A1A8C3] font-light max-w-2xl mx-auto">
            This is not a one-time database build. It is a continuously compounding intelligence cycle
            that gets smarter and more valuable with every conference we attend.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {loop.map((s, i) => (
            <div key={i} className={`relative bg-[#0A1128]/60 border border-white/5 rounded-2xl p-8 hover:border-white/15 transition-all duration-300 ${inView ? `reveal-up stagger-${i + 1}` : 'opacity-0'}`}>
              <div className="absolute top-6 right-6 text-4xl font-black text-white/5">0{i + 1}</div>
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: `${s.color}15`, color: s.color }}>
                {s.icon}
              </div>
              <h4 className="text-lg font-bold text-white mb-3">{s.title}</h4>
              <p className="text-sm text-[#A1A8C3] font-light leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ForPharma() {
  const [ref, inView] = useInView();
  const steps = [
    { icon: <Target className="w-5 h-5" />, title: 'Define your BD thesis', desc: 'Tell us your therapeutic areas, preferred stage, modality, geography, and deal structure. Your thesis stays confidential — biotechs never see it.' },
    { icon: <Search className="w-5 h-5" />, title: 'We scout on the ground', desc: 'At conferences worldwide, our team meets biotechs, assesses their science, and identifies the assets that fit your thesis — pre-qualified before you see them.' },
    { icon: <FileText className="w-5 h-5" />, title: 'Curated intelligence, not raw data', desc: 'You receive qualified asset profiles with rationale, competitive context, and deal-readiness assessment. Discovery access via platform — not a database dump.' },
    { icon: <Handshake className="w-5 h-5" />, title: 'We facilitate the introduction', desc: 'When you are ready to engage, we handle the positioning, confidential outreach, NCD exchange, and CDA facilitation. This is where the platform ends and the deal begins.' },
    { icon: <BarChart3 className="w-5 h-5" />, title: 'Deal support to close', desc: 'Meeting prep, due diligence dossiers, term benchmarking — we stay in the loop as the trusted bridge between both sides until the deal is done.' },
  ];

  return (
    <section ref={ref} className="py-24 lg:py-32 bg-[#0A1128]" data-testid="for-pharma-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className={`grid lg:grid-cols-2 gap-16 items-start ${inView ? 'reveal-up' : 'opacity-0'}`}>
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00709B]/15 text-[#00709B] text-xs font-bold uppercase tracking-wider mb-6">
              <Building2 className="w-3.5 h-3.5" /> For Pharma & Large Biotech
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-6">
              Stop paying for
              <br />
              dead databases.
            </h2>
            <p className="text-[#A1A8C3] font-light leading-relaxed mb-8">
              Your BD team spends weeks scrolling databases that were last updated months ago.
              We give you continuously refreshed, conference-sourced, AI-matched biotech assets —
              with context only boots-on-ground intelligence can provide.
            </p>
            <Link to="/apply" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#00709B] text-white text-sm font-bold hover:bg-[#005A7A] transition-all duration-300" data-testid="pharma-apply-btn">
              Explore Pharma Solutions <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="space-y-4">
            {steps.map((s, i) => (
              <div key={i} className={`flex gap-5 p-5 rounded-2xl bg-[#111A3A]/80 border border-white/5 hover:border-[#00709B]/20 transition-all duration-300 ${inView ? `reveal-up stagger-${i + 1}` : 'opacity-0'}`} data-testid={`pharma-step-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-[#00709B]/15 flex items-center justify-center text-[#00709B] shrink-0">{s.icon}</div>
                <div>
                  <h4 className="text-sm font-bold text-white mb-1">{s.title}</h4>
                  <p className="text-sm text-[#A1A8C3] font-light leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ForBiotech() {
  const [ref, inView] = useInView();
  const steps = [
    { icon: <FlaskConical className="w-5 h-5" />, title: 'Asset positioning', desc: 'We sharpen your narrative: problem-patient-proof. Your science, reframed for the audience that actually writes cheques.' },
    { icon: <FileText className="w-5 h-5" />, title: 'Evidence & value blueprint', desc: 'Epidemiology, competitive landscape, commercial scenarios — the intelligence pharma BD teams expect before saying yes.' },
    { icon: <Target className="w-5 h-5" />, title: 'Curated target list', desc: 'We identify the specific pharma companies most likely to care about your asset — based on their BD thesis, not guesswork.' },
    { icon: <Handshake className="w-5 h-5" />, title: 'Protected introductions', desc: 'Your confidential data stays with us until you approve each introduction. Pharma sees only what you want them to see, when you are ready.' },
    { icon: <Zap className="w-5 h-5" />, title: 'No upfront fees', desc: 'Success-based model. We invest our time and resources first. You pay when a deal happens. Our incentives are fully aligned with yours.' },
  ];

  return (
    <section ref={ref} className="py-24 lg:py-32 bg-[#111A3A]" data-testid="for-biotech-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className={`grid lg:grid-cols-2 gap-16 items-start ${inView ? 'reveal-up' : 'opacity-0'}`}>
          <div className="lg:order-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#70C24C]/10 text-[#70C24C] text-xs font-bold uppercase tracking-wider mb-6">
              <FlaskConical className="w-3.5 h-3.5" /> For Biotech Companies
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-6">
              Stop burning cash
              <br />
              on conference parties.
            </h2>
            <p className="text-[#A1A8C3] font-light leading-relaxed mb-8">
              You spend $50K on a conference booth, hand out NCDs to everyone who walks by, and wait months
              for one callback. We put your asset directly in front of the pharma BD teams that are actively
              looking for what you have — without you having to leave your lab.
            </p>
            <Link to="/partnering" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#70C24C] text-[#0A1128] text-sm font-bold hover:bg-[#5fad3b] transition-all duration-300 shadow-[0_0_20px_rgba(112,194,76,0.2)]" data-testid="biotech-apply-btn">
              Explore Partnering <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="space-y-4 lg:order-1">
            {steps.map((s, i) => (
              <div key={i} className={`flex gap-5 p-5 rounded-2xl bg-[#0A1128]/60 border border-white/5 hover:border-[#70C24C]/20 transition-all duration-300 ${inView ? `reveal-up stagger-${i + 1}` : 'opacity-0'}`} data-testid={`biotech-step-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-[#70C24C]/10 flex items-center justify-center text-[#70C24C] shrink-0">{s.icon}</div>
                <div>
                  <h4 className="text-sm font-bold text-white mb-1">{s.title}</h4>
                  <p className="text-sm text-[#A1A8C3] font-light leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function NetworkEffect() {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} className="py-24 lg:py-32 bg-[#0A1128] relative overflow-hidden" data-testid="network-effect">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-[#00709B]/8 to-[#70C24C]/8 blur-[100px]" />
      <div className={`relative max-w-4xl mx-auto px-6 lg:px-12 text-center ${inView ? 'reveal-up' : 'opacity-0'}`}>
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-4">The Compounding Advantage</p>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-6">
          Every conference makes the next deal better
        </h2>
        <p className="text-lg text-[#A1A8C3] font-light leading-relaxed mb-12 max-w-2xl mx-auto">
          More conferences attended. More biotechs qualified. More pharma theses mapped.
          Better matching. Faster deals. A compounding intelligence advantage that no
          static database can replicate.
        </p>
        <div className="grid sm:grid-cols-4 gap-6">
          {[
            { label: 'Conferences Attended', val: 'More ground truth', color: '#70C24C' },
            { label: 'Biotechs Qualified', val: 'Richer supply data', color: '#00709B' },
            { label: 'Pharma Theses', val: 'More demand signals', color: '#70C24C' },
            { label: 'Deals Facilitated', val: 'Smarter matching', color: '#00709B' },
          ].map((item, i) => (
            <div key={i} className={`p-6 rounded-2xl bg-[#111A3A] border border-white/5 ${inView ? `reveal-up stagger-${i + 1}` : 'opacity-0'}`}>
              <div className="w-3 h-3 rounded-full mb-4 mx-auto" style={{ backgroundColor: item.color }} />
              <p className="text-sm font-bold text-white mb-1">{item.label}</p>
              <p className="text-xs text-[#687399]">{item.val}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PlatformCTA() {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} className="py-24 lg:py-32 bg-[#111A3A]" data-testid="platform-cta">
      <div className={`max-w-3xl mx-auto px-6 lg:px-12 text-center ${inView ? 'reveal-up' : 'opacity-0'}`}>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-6">
          Ready to experience living intelligence?
        </h2>
        <p className="text-lg text-[#A1A8C3] font-light mb-10">
          Tell us what you are looking for — we will come back with an honest assessment of
          whether and how we can help. No sales pitch. Just substance.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/apply" className="inline-flex items-center gap-2 px-10 py-4 rounded-full bg-[#70C24C] text-[#0A1128] text-base font-bold hover:bg-[#5fad3b] hover:-translate-y-0.5 transition-all duration-300 shadow-[0_0_30px_rgba(112,194,76,0.25)]" data-testid="platform-cta-btn">
            Get Started <ArrowRight className="w-5 h-5" />
          </Link>
          <Link to="/services" className="inline-flex items-center gap-2 px-10 py-4 rounded-full border border-white/15 text-white text-base font-medium hover:bg-white/5 transition-all duration-300" data-testid="platform-services-btn">
            View Pricing
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function PlatformPage() {
  useSEO({
    title: 'AI Partnering Platform — How the Intelligence Engine Works',
    description: 'See how ForesightRx combines boots-on-ground conference intelligence with AI-powered matching and deal facilitation. Purpose-built pharma business development tool for in-licensing and out-licensing.',
    canonical: 'https://foresightrx.com/platform',
  });
  usePageTracker('Platform');

  return (
    <div className="min-h-screen bg-[#0A1128] copy-protect">
      <PublicNav />
      <PlatformHero />
      <TheLivingLoop />
      <ForPharma />
      <ForBiotech />
      <NetworkEffect />
      <PlatformCTA />
      <PublicFooter />
    </div>
  );
}
